import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import { formatUtcDate } from "@/app/_utils/dayjs";
import type { Festival } from "@/app/_services/FestivalService";
import { cn } from "@/app/_utils/cn";
import { MoreHorizontal, Pencil, Trash2, PartyPopper, Eye, EyeOff, Ticket, Clock } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

export default function FestivalList({
  festivals,
  onEdit,
  onDelete,
  onTogglePublish,
}: {
  festivals: Festival[];
  onEdit: (festival: Festival) => void;
  onDelete: (festival: Festival) => void;
  onTogglePublish: (festival: Festival) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Festival | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const rowMenu = (festival: Festival) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(festival)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onTogglePublish(festival)}>
          {festival.published ? <EyeOff className="mr-2 size-4" /> : <Eye className="mr-2 size-4" />}
          {festival.published ? "Unpublish" : "Publish"}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={() => setPendingDelete(festival)}
        >
          <Trash2 className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {festivals.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={PartyPopper} text="No festivals" />
          </div>
        ) : (
          <div className="flex flex-col">
            {festivals.map((festival) => (
              <div
                key={festival.id}
                className="flex cursor-pointer items-start gap-2.5 border-b border-border px-4 py-3 last:border-0"
                onClick={() => navigate(`/admin/festivals/${festival.id}`)}
              >
                <ShowThumbnail
                  src={festival.img_url}
                  style={{ height: 48, width: 48, borderRadius: 6, flexShrink: 0 }}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-[14px] font-semibold text-foreground">
                      {festival.title}
                    </span>
                    <div className="flex flex-none items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`gap-1.5 rounded-[7px] font-normal ${
                          festival.published
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-50 text-slate-600"
                        }`}
                      >
                        <span
                          className={`size-1.5 flex-none rounded-full ${
                            festival.published ? "bg-emerald-500" : "bg-slate-400"
                          }`}
                        />
                        {festival.published ? "Published" : "Unpublished"}
                      </Badge>
                      <div onClick={(e) => e.stopPropagation()}>{rowMenu(festival)}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                    <PartyPopper className="size-3.5 flex-none" />
                    {festival.shows?.length ?? 0} show{(festival.shows?.length ?? 0) === 1 ? "" : "s"}
                  </div>

                  <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                    <Ticket className="size-3.5 flex-none" />
                    <div className="h-1.5 max-w-[140px] flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{ width: `${Math.min(100, festival.sell_through_pct)}%` }}
                      />
                    </div>
                    <span className="whitespace-nowrap font-medium">
                      {festival.tickets_sold}/{festival.tickets_available}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Clock className="size-3.5 flex-none" />
                    Updated {formatUtcDate(festival.updated_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <CuoConfirmDialog
          open={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          title="Delete festival"
          message="This action cannot be undone. Are you sure you want to delete this festival?"
          onConfirm={async () => {
            if (pendingDelete) await onDelete(pendingDelete);
          }}
          icon={<Trash2 />}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", festivals.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-1/4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Title
            </TableHead>
            <TableHead className="h-8 w-16 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </TableHead>
            <TableHead className="h-8 w-[13%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Sell-through
            </TableHead>
            <TableHead className="h-8 w-[17%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Updated
            </TableHead>
            <TableHead className="h-8 w-12 pr-3 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {festivals.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={6} className="h-full p-0">
                <CuoEmpty icon={PartyPopper} text="No festivals" />
              </TableCell>
            </TableRow>
          ) : (
            festivals.map((festival) => (
              <TableRow
                key={festival.id}
                className="group cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/admin/festivals/${festival.id}`)}
              >
                <TableCell className="truncate py-2.5 text-[13px] text-foreground">
                  <span className="group-hover:underline">{festival.title}</span>
                </TableCell>
                <TableCell className="whitespace-nowrap py-2.5 text-center text-[12.5px] text-muted-foreground">
                  {festival.shows?.length ?? 0}
                </TableCell>
                <TableCell className="py-2.5">
                  <Badge
                    variant="outline"
                    className={`gap-1.5 rounded-[7px] font-normal ${
                      festival.published
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    <span
                      className={`size-1.5 flex-none rounded-full ${
                        festival.published ? "bg-emerald-500" : "bg-slate-400"
                      }`}
                    />
                    {festival.published ? "Published" : "Unpublished"}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-1.5 max-w-[140px] flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{ width: `${Math.min(100, festival.sell_through_pct)}%` }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-[12px] font-medium text-muted-foreground">
                      {festival.tickets_sold}/{festival.tickets_available}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap py-2.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(festival.updated_at)}
                </TableCell>
                <TableCell className="py-2.5 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {rowMenu(festival)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CuoConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete festival"
        message="This action cannot be undone. Are you sure you want to delete this festival?"
        onConfirm={async () => {
          if (pendingDelete) await onDelete(pendingDelete);
        }}
        icon={<Trash2 />}
      />
    </div>
  );
}
